import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Lightbulb, Activity, FlaskConical, ShieldCheck } from 'lucide-react';
import ResponsibleAI from '../features/responsibleAI/ResponsibleAI';
import AgentWorkflow from '../features/AgentWorkflow/AgentWorkflow';

const RAI_SUBTABS = [
  { id: 'customers', label: 'Initiatives', icon: Lightbulb, slug: 'initiatives' },
  { id: 'groups', label: 'Monitoring', icon: Activity, slug: 'monitoring' },
  { id: 'modelEvaluation', label: 'Model Evaluation', icon: FlaskConical, slug: 'model-evaluation', description: 'Comprehensive LLM evaluation tool that measures model accuracy, reliability, and error rates across various metrics.' },
  { id: 'guardrails', label: 'Guardrails', icon: ShieldCheck, slug: 'guardrails', description: 'Create and manage guardrails configurations for your AI applications. Guardrails help ensure your AI responses are safe, accurate, and compliant with your policies.' },
];

const SLUG_TO_ID = Object.fromEntries(RAI_SUBTABS.map(t => [t.slug, t.id]));

const AIGovernancePage = () => {
  const location = useLocation();
  const { tab } = useParams();
  const navigate = useNavigate();
  const isAgentLineage = location.pathname === '/ai-governance/agent-lineage';

  const raiSubTab = SLUG_TO_ID[tab] || 'customers';
  const activeTabData = RAI_SUBTABS.find(t => t.id === raiSubTab);

  const setRaiSubTab = (tabId) => {
    const slug = RAI_SUBTABS.find(t => t.id === tabId)?.slug || 'initiatives';
    navigate(`/ai-governance/responsible-ai/${slug}`);
  };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Header — fixed at top, never scrolls */}
      <div className="flex-none bg-gray-100 px-4 sm:px-6 shadow-sm relative z-[45]">
        <div className="pt-4 pb-3 border-b border-gray-300">
          <h1 className="text-2xl font-bold text-primary">
            {isAgentLineage ? 'Agent Lineage' : 'Responsible AI'}
          </h1>
        </div>
        {!isAgentLineage && (
          <div className="pt-2 pb-0">
            <nav className="-mb-px flex gap-6 overflow-x-auto scrollbar-none">
              {RAI_SUBTABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setRaiSubTab(id)}
                  className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap shrink-0 ${
                    raiSubTab === id
                      ? 'border-secondary text-secondary font-semibold'
                      : 'border-transparent text-gray-700 hover:text-primaryDark hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        )}
        {!isAgentLineage && activeTabData?.description && (
          <p className="pl-2 pr-4 sm:pr-6 pt-3 sm:pt-4 pb-3 sm:pb-4 text-[13px] sm:text-[14px] italic text-gray-600 w-full max-w-[680px]">{activeTabData.description}</p>
        )}
      </div>

      {/* Content — scrolls independently inside its own area */}
      <div className="flex-1 overflow-y-auto pb-6">
        {isAgentLineage
          ? (
            <div className="min-h-full bg-gray-50">
              <p className="px-6 pt-4 pb-0 text-[0.8rem] italic text-gray-600">
                Interact with the chatbot to see how the agent processes your request in real-time. The workflow graph below will update after each interaction.
              </p>
              <AgentWorkflow />
            </div>
          )
          : <ResponsibleAI activeTab={raiSubTab} setActiveTab={setRaiSubTab} />
        }
      </div>
    </div>
  );
};

export default AIGovernancePage;
